import React, { useState, useEffect } from 'react'
import {
  ChevronDown,
  ChevronRight,
  Loader2,
  AlertCircle,
  AlertTriangle,
  Building2,
  Plug,
  Users,
  Calendar,
  Clock,
  ExternalLink,
  RefreshCw
} from 'lucide-react'
import { organizationService, EnhancedApiOrg, EnhancedApiAccount } from '../../services/organizationService'
import OrganizationTabbedDrawer from './OrganizationTabbedDrawer'

const formatDate = (value: string | null): string => {
  if (!value) return '—'
  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
}

/** Days until a date — negative when already past. Null when there's no date. */
const daysUntil = (value: string | null): number | null => {
  if (!value) return null
  const ms = new Date(value).getTime() - Date.now()
  return Math.ceil(ms / (1000 * 60 * 60 * 24))
}

const TOKEN_STATUS_STYLES: Record<string, string> = {
  active: 'bg-green-100 text-green-700',
  expiring_soon: 'bg-amber-100 text-amber-700',
  expired: 'bg-red-100 text-red-700',
  revoked: 'bg-gray-100 text-gray-600'
}

const TokenStatusChip: React.FC<{ status: string }> = ({ status }) => (
  <span
    className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
      TOKEN_STATUS_STYLES[status] ?? 'bg-gray-100 text-gray-600'
    }`}
  >
    {status.replace(/_/g, ' ')}
  </span>
)

const AccountTile: React.FC<{ account: EnhancedApiAccount }> = ({ account }) => {
  const remaining = daysUntil(account.tokenExpiresAt)

  // An active OAuth row with the platform flag off means enrichment skips this
  // account — the most common cause of "Enhanced API is connected but no data".
  const flagMismatch = !account.platformAccountFlagged

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-3 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs font-semibold text-gray-900 truncate">
            @{account.username ?? account.providerUsername ?? 'unknown'}
          </p>
          {account.displayName && (
            <p className="text-[11px] text-gray-500 truncate">{account.displayName}</p>
          )}
        </div>
        <TokenStatusChip status={account.tokenStatus} />
      </div>

      <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
        <Plug className="h-3 w-3 flex-shrink-0" />
        <span>{account.platformDisplayName ?? account.provider}</span>
      </div>

      <div className="grid grid-cols-2 gap-2 pt-1 border-t border-gray-100">
        <div>
          <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1">
            <Calendar className="h-2.5 w-2.5" /> Enabled
          </p>
          <p className="text-[11px] text-gray-900 mt-0.5">{formatDate(account.connectedAt)}</p>
        </div>
        <div>
          <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1">
            <Clock className="h-2.5 w-2.5" /> Expires
          </p>
          <p className="text-[11px] text-gray-900 mt-0.5">
            {formatDate(account.tokenExpiresAt)}
            {remaining !== null && (
              <span
                className={`ml-1 ${
                  remaining < 0
                    ? 'text-red-600'
                    : remaining <= 14
                      ? 'text-amber-600'
                      : 'text-gray-400'
                }`}
              >
                ({remaining < 0 ? `${Math.abs(remaining)}d ago` : `${remaining}d`})
              </span>
            )}
          </p>
        </div>
      </div>

      {account.tokenRefreshedAt && (
        <p className="text-[10px] text-gray-400 flex items-center gap-1">
          <RefreshCw className="h-2.5 w-2.5" />
          Last refreshed {formatDate(account.tokenRefreshedAt)}
        </p>
      )}

      {flagMismatch && (
        <div className="flex items-start gap-1.5 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
          <AlertTriangle className="h-3 w-3 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-[10px] text-amber-800 leading-snug">
            Platform account flag is off — enrichment will skip this account. Reconnect to fix.
          </p>
        </div>
      )}
    </div>
  )
}

const OrgRow: React.FC<{
  org: EnhancedApiOrg
  onOpenDrawer: (orgId: string) => void
}> = ({ org, onOpenDrawer }) => {
  const [expanded, setExpanded] = useState(false)

  const needsAttention = org.accounts.some(
    (a) => a.tokenStatus === 'expired' || !a.platformAccountFlagged
  )

  return (
    <>
      <tr
        className="hover:bg-gray-50 transition-colors cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <td className="px-3 py-2.5">
          <div className="flex items-center gap-1.5">
            {expanded
              ? <ChevronDown className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
              : <ChevronRight className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
            }
            <span className="text-xs font-medium text-gray-900">{org.organizationName}</span>
            {needsAttention && (
              <span title="Has an expired token or a platform flag mismatch" className="flex-shrink-0">
                <AlertTriangle className="h-3 w-3 text-amber-500" />
              </span>
            )}
          </div>
        </td>
        <td className="px-3 py-2.5">
          <span className="text-xs text-gray-600">{org.customerName ?? '—'}</span>
        </td>
        <td className="px-3 py-2.5">
          <span className="text-xs text-gray-600">{org.subscriptionTierName ?? '—'}</span>
        </td>
        <td className="px-3 py-2.5 text-right">
          <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-900">
            <Users className="h-3 w-3 text-gray-400" />
            {org.connectedAccountCount}
          </span>
        </td>
        <td className="px-3 py-2.5 text-right">
          <span className="text-xs text-gray-500">{formatDate(org.firstConnectedAt)}</span>
        </td>
        <td className="px-3 py-2.5 text-center">
          <button
            onClick={(e) => { e.stopPropagation(); onOpenDrawer(org.organizationId) }}
            className="p-1 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
            title="Open organization"
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </button>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={6} className="bg-gray-50 border-b border-gray-200">
            <div className="p-3">
              <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2">
                Connected Accounts ({org.connectedAccountCount})
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {org.accounts.map((account) => (
                  <AccountTile key={account.oauthAccountId} account={account} />
                ))}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

const EnhancedApiAdoptionView: React.FC = () => {
  const [data, setData] = useState<{
    totalOrganizations: number
    totalAccounts: number
    organizations: EnhancedApiOrg[]
  } | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [drawerOrg, setDrawerOrg] = useState<any | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  useEffect(() => {
    setLoading(true)
    setError(null)
    organizationService.getEnhancedApiAdoption().then((res) => {
      if (res.success) {
        setData(res.data)
      } else {
        setError('Failed to load Enhanced API connection data')
      }
    }).catch(() => {
      setError('Failed to load Enhanced API connection data')
    }).finally(() => setLoading(false))
  }, [])

  const handleOpenDrawer = async (orgId: string) => {
    try {
      const res = await organizationService.getOrganization(orgId)
      setDrawerOrg(res.data ?? null)
    } catch {
      setDrawerOrg(null)
    }
    setDrawerOpen(true)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-sky-100 rounded-lg">
            <Plug className="h-5 w-5 text-sky-600" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Enhanced API Connections</h2>
            <p className="text-xs text-gray-500">
              Organizations that have completed the Instagram Graph API OAuth flow, and the social
              accounts connected under each
            </p>
          </div>
        </div>

        {data && !loading && (
          <div className="flex items-center gap-6 mt-4 pt-4 border-t border-gray-100">
            <div>
              <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Organizations</p>
              <p className="text-lg font-bold text-gray-900">{data.totalOrganizations}</p>
            </div>
            <div>
              <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Connected Accounts</p>
              <p className="text-lg font-bold text-gray-900">{data.totalAccounts}</p>
            </div>
          </div>
        )}
      </div>

      {/* Results */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-7 w-7 animate-spin text-blue-600" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center mb-3">
              <AlertCircle className="h-5 w-5 text-red-600" />
            </div>
            <p className="text-sm font-semibold text-gray-900 mb-1">Failed to load data</p>
            <p className="text-xs text-gray-500">{error}</p>
          </div>
        ) : data && data.organizations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center mb-3">
              <Building2 className="h-5 w-5 text-gray-400" />
            </div>
            <p className="text-sm font-semibold text-gray-900 mb-1">No connections</p>
            <p className="text-xs text-gray-500">
              No organizations have connected an account via the Enhanced API yet
            </p>
          </div>
        ) : data ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider px-3 py-2">Organization</th>
                  <th className="text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider px-3 py-2">Customer</th>
                  <th className="text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider px-3 py-2">Tier</th>
                  <th className="text-right text-[10px] font-semibold text-gray-600 uppercase tracking-wider px-3 py-2">Accounts</th>
                  <th className="text-right text-[10px] font-semibold text-gray-600 uppercase tracking-wider px-3 py-2">First Connected</th>
                  <th className="text-center text-[10px] font-semibold text-gray-600 uppercase tracking-wider px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {data.organizations.map((org) => (
                  <OrgRow key={org.organizationId} org={org} onOpenDrawer={handleOpenDrawer} />
                ))}
              </tbody>
            </table>
            <p className="text-[10px] text-gray-400 px-4 py-2 border-t border-gray-100">
              Click a row to expand and view connected accounts. Shows currently connected accounts only —
              revoked connections are excluded.
            </p>
          </div>
        ) : null}
      </div>

      {/* Organization drawer */}
      <OrganizationTabbedDrawer
        isOpen={drawerOpen}
        onClose={() => { setDrawerOpen(false); setDrawerOrg(null) }}
        organization={drawerOrg}
        isCreating={false}
        onSaved={() => {}}
      />
    </div>
  )
}

export default EnhancedApiAdoptionView
